import Types from '../Types'
import Immutable from 'seamless-immutable'
import { createReducer } from 'reduxsauce'

// Helpers
import _get from 'lodash.get'
import _keyBy from 'lodash.keyby'
import _groupBy from 'lodash.groupby'
import _merge from 'lodash.merge'

export const INITIAL_STATE = Immutable({
  error: null,
  checkoutError: null,
  findAttempting: false,
  findAllAttempting: false,
  pageCount: 0,
  orders: [],
  addToCartAttempting: false,
  addToCartAttempted: false,
  cart: {},
  cartProducts: [],
  cartProductMap: {},
  checkoutAttempting: false,
  checkoutAttempted: false
})

const mutateProducts = (result) =>
  (product) =>
    result.productCode === product.productCode
      ? { ...product, userId: result.userId }
      : product

const ACTION_HANDLERS = {
  [Types.FIND_ORDERS_ATTEMPT]: (state, action) =>
    state.merge({ findAttempting: true }),
  [Types.FIND_ORDERS_SUCCESS]: (state, action) =>
    state.merge({
      findAttempting: false,
      orders: action.result,
      error: null
    }),
  [Types.FIND_ORDERS_FAILURE]: (state, action) =>
    state.merge({ findAttempting: false, error: action.error }),

  [Types.FIND_ALL_ORDERS_ATTEMPT]: (state, action) =>
    state.merge({ findAllAttempting: true }),
  [Types.FIND_ALL_ORDERS_SUCCESS]: (state, action) =>
    state.merge({
      findAllAttempting: false,
      pageCount: action.result.pageCount || (action.result.orders.length ? state.pageCount : 0),
      orders: ((orderGroups) =>
        Object.keys(orderGroups).map(key => {
          const products = orderGroups[key]
          const { orderId, orderCode, deliveryName, deliveryContactNumber, checkedOutAt, status, paidStatus, deliveryStatus, invoicePath, pharmacyName, promoCode, promoUnit, promoDiscount, docHash } = products[0]
          const formattedProducts = orderGroups[key].map(({ productCode, productName, unit, unitId, storage, amount, currentPrice, wholesale1, wholesale2, wholesale3, wholesale4 }) => ({
            productCode,
            productName,
            unit,
            unitId,
            storage: storage || '',
            amount: parseInt(amount, 10),
            currentPrice: parseFloat(currentPrice),
            wholesale1: parseFloat(wholesale1),
            wholesale2: parseFloat(wholesale2),
            wholesale3: parseFloat(wholesale3),
            wholesale4: parseFloat(wholesale4)
          }))
            .sort((a, b) => a.productName.localeCompare(b.productName) || (a.unitId - b.unitId))
            .slice()

          return {
            orderId,
            orderCode,
            deliveryName,
            deliveryContactNumber,
            checkedOutAt,
            status,
            paidStatus,
            deliveryStatus,
            invoicePath,
            pharmacyName,
            promoCode,
            promoUnit,
            promoDiscount,
            docHash,
            products: formattedProducts.filter(({ currentPrice }) => currentPrice)
              .concat(formattedProducts.filter(({ currentPrice }) => !currentPrice))
          }
        })
      )(_groupBy(action.result.orders, product => product.orderId)),
      error: null
    }),
  [Types.FIND_ALL_ORDERS_FAILURE]: (state, action) =>
    state.merge({ findAllAttempting: false, error: action.error }),

  [Types.SAVE_ORDER_ATTEMPT]: (state, action) =>
    state.merge({ saveAttempting: true }),
  [Types.SAVE_ORDER_SUCCESS]: (state, action) =>
    state.merge({
      saveAttempting: false,
      orders: state.orders.map(order =>
        _merge(order.orderId === action.result.orderId ? { ...action.result, pharmacyName: order.pharmacyName } : order, { products: order.products })
      ),
      error: null
    }),
  [Types.SAVE_ORDER_FAILURE]: (state, action) =>
    state.merge({ saveAttempting: false, error: action.error }),

  [Types.REORDER_ATTEMPT]: (state, action) =>
    state.merge({ addToCartAttempting: true, addToCartAttempted: false }),
  [Types.REORDER_SUCCESS]: (state, action) =>
    state.merge({
      addToCartAttempting: false,
      addToCartAttempted: true,
      cartProducts: action.result,
      error: null
    }),
  [Types.REORDER_FAILURE]: (state, action) =>
    state.merge({ addToCartAttempting: false, addToCartAttempted: false, error: action.error }),

  [Types.GET_CART_ATTEMPT]: (state, action) =>
    state.merge({ getCartAttempting: true }),
  [Types.GET_CART_SUCCESS]: (state, action) =>
    state.merge({
      getCartAttempting: false,
      cart: _get(action, ['result', 'cart']),
      cartProducts: _get(action, ['result', 'products']),
      cartProductMap: _keyBy(_get(action, ['result', 'products']), ({ productCode, unitId }) => `${productCode}-${unitId}`),
      error: null
    }),
  [Types.GET_CART_FAILURE]: (state, action) =>
    state.merge({ getCartAttempting: false, error: action.error }),

  [Types.ADD_TO_CART_ATTEMPT]: (state, action) =>
    state.merge({ addToCartAttempting: true, addToCartAttempted: false }),
  [Types.ADD_TO_CART_SUCCESS]: (state, action) => {
    const isSameProduct = (_product) =>
      _product.productCode === action.result.productCode && _product.unitId === action.result.unitId
    return state.merge({
      addToCartAttempting: false,
      addToCartAttempted: true,
      cartProducts: state.cartProducts
        // Replace previous product with the same product code and unitId with added product.
        .map(_product => isSameProduct(_product) ? action.result : _product)
        // If added product not already exists int the card, add it.
        .concat(state.cartProducts.filter(_product => isSameProduct(_product)).length === 0 ? [action.result] : [])
        // Filter only those items with amount more than 0.
        .filter(product => product.amount > 0),
      cartProductMap: state.cartProductMap.merge({ [`${action.result.productCode}-${action.result.unitId}`]: action.result }),
      error: null
    })
  },
  [Types.ADD_TO_CART_FAILURE]: (state, action) =>
    state.merge({ addToCartAttempting: false, addToCartAttempted: false, error: action.error }),

  [Types.SAVE_CART_ATTEMPT]: (state, action) =>
    state.merge({ saveCartAttempting: true }),
  [Types.SAVE_CART_SUCCESS]: (state, action) =>
    state.merge({
      saveCartAttempting: false,
      cartProducts: _get(action, ['result']),
      cartProductMap: _keyBy(_get(action, ['result']), ({ productCode, unitId }) => `${productCode}-${unitId}`)
    }),
  [Types.SAVE_CART_FAILURE]: (state, action) =>
    state.merge({ saveCartAttempting: false, error: action.error }),

  [Types.CHECKOUT_ATTEMPT]: (state, action) =>
    state.merge({ checkoutAttempting: true, checkoutAttempted: false }),
  [Types.CHECKOUT_SUCCESS]: (state, action) =>
    state.merge({ checkoutAttempting: false, checkoutAttempted: true, cart: {}, cartProducts: [], cartProductMap: {} }),
  [Types.CHECKOUT_FAILURE]: (state, action) =>
    state.merge({ checkoutAttempting: false, checkoutAttempted: false, checkoutError: action.error }),

  [Types.UPLOAD_INVOICE_ATTEMPT]: (state, action) =>
    state.merge({ uploadInvoiceAttempting: true, uploadInvoiceAttempted: false }),
  [Types.UPLOAD_INVOICE_SUCCESS]: (state, action) =>
    state.merge({
      uploadInvoiceAttempting: false,
      uploadInvoiceAttempted: true,
      orders: state.orders.map(order =>
        _merge(order.orderId === action.result.orderId ? action.result : order, { products: order.products })
      )
    }),
  [Types.UPLOAD_INVOICE_FAILURE]: (state, action) =>
    state.merge({ uploadInvoiceAttempting: false, uploadInvoiceAttempted: false, error: action.error }),

  [Types.ADD_TO_WISHLIST_SUCCESS]: (state, action) =>
    state.merge({
      cartProducts: state.cartProducts.map(mutateProducts(action.result)),
      cartProductMap: _keyBy(state.cartProducts.map(mutateProducts(action.result)), ({ productCode, unitId }) => `${productCode}-${unitId}`)
    }),
  [Types.REMOVE_FROM_WISHLIST_SUCCESS]: (state, action) =>
    state.merge({
      cartProducts: state.cartProducts.map(mutateProducts(action.result)),
      cartProductMap: _keyBy(state.cartProducts.map(mutateProducts(action.result)), ({ productCode, unitId }) => `${productCode}-${unitId}`)
    }),

  [Types.CREATE_PACK_PDF_ATTEMPT]: (state, action) =>
    state.merge({ createPackPdfAttempting: true, createPackPdfAttempted: false }),
  [Types.CREATE_PACK_PDF_SUCCESS]: (state, action) =>
    state.merge({ createPackPdfAttempting: false, createPackPdfAttmpted: true }),
  [Types.CREATE_PACK_PDF_FAILURE]: (state, action) =>
    state.merge({ createPackPdfAttempting: false, createPackPdfAttmpted: false, error: action.error }),

  [Types.CREATE_CW_ORDER_ATTEMPT]: (state, action) =>
    state.merge({ createCwOrderAttempting: true, createCwOrderAttempted: false }),
  [Types.CREATE_CW_ORDER_SUCCESS]: (state, action) =>
    state.merge({
      createCwOrderAttempting: false,
      createdCwOrderAttempted: true,
      orders: state.orders.map(order =>
        order.orderId === action.result.orderId
          ? { ...order, orderCode: action.result.orderCode }
          : order
      )
    }),
  [Types.CREATE_CW_ORDER_FAILURE]: (state, action) =>
    state.merge({ createCwOrderAttempting: false, createCwOrderAttempted: false })
}

export default createReducer(INITIAL_STATE, ACTION_HANDLERS)



// WEBPACK FOOTER //
// ./src/redux/order/orderReducer.js